import React from "react";
import { Typography } from "@material-tailwind/react";

/**
 * Contenedor consistente para títulos de página + descripción opcional.
 * @param {boolean} [wide] — sin max-width estrecho (p. ej. tablas de admin).
 * @param {string} [headerBackgroundImage] — imagen de fondo solo en el bloque título + subtítulo.
 * @param {boolean} [headerBackgroundCompact] — hero más bajo (p. ej. panel).
 * @param {boolean} [headerImageOnly] — solo imagen de fondo, sin título ni subtítulo en el hero.
 */
export function PortalShell({
  title,
  subtitle,
  children,
  className = "",
  wide = false,
  headerBackgroundImage,
  headerBackgroundCompact = false,
  headerImageOnly = false,
}) {
  const titleClass = headerBackgroundImage
    ? headerBackgroundCompact
      ? "font-sans text-white text-2xl sm:text-3xl md:text-4xl font-light tracking-[0.08em] drop-shadow-md"
      : "font-sans text-white text-3xl sm:text-4xl md:text-5xl font-light tracking-[0.08em] drop-shadow-md"
    : "font-sans text-brand-dark text-2xl sm:text-3xl font-semibold tracking-tight";

  const subtitleClass = headerBackgroundImage
    ? headerBackgroundCompact
      ? "mt-3 text-white/95 text-sm sm:text-base max-w-2xl leading-relaxed tracking-wide drop-shadow-sm"
      : "mt-4 text-white/95 text-base sm:text-lg max-w-2xl leading-relaxed tracking-wide drop-shadow-sm"
    : "mt-2 text-brand-gray text-sm sm:text-base max-w-xl leading-relaxed";

  const contentWidth = wide ? "max-w-none w-full" : "max-w-3xl w-full";

  const headerInner =
    !headerImageOnly && (title || subtitle) ? (
      <>
        {title ? (
          <Typography variant="h3" className={titleClass}>
            {title}
          </Typography>
        ) : null}
        {subtitle && <p className={subtitleClass}>{subtitle}</p>}
      </>
    ) : null;

  if (headerBackgroundImage) {
    const heroMinH = headerImageOnly
      ? headerBackgroundCompact
        ? "min-h-[12rem] sm:min-h-[16rem]"
        : "min-h-[16rem] sm:min-h-[22rem]"
      : headerBackgroundCompact
        ? "min-h-[14rem] sm:min-h-[18rem]"
        : "min-h-[18rem] sm:min-h-[23rem]";
    const heroPy = headerImageOnly
      ? ""
      : headerBackgroundCompact
        ? "py-10 sm:py-12"
        : "py-14 sm:py-16";

    return (
      <>
        <header className="mb-6 sm:mb-8 -mt-8 sm:-mt-10 relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen max-w-[100vw]">
          <div className={`relative flex ${heroMinH} items-center overflow-hidden`}>
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('${headerBackgroundImage}')` }}
              aria-hidden
            />
            <div className="absolute inset-0 bg-brand-dark/50" aria-hidden />
            {headerInner ? (
              <div
                className={`relative mx-auto w-full min-w-0 px-3 sm:px-4 md:px-6 ${heroPy} ${contentWidth}`}
              >
                {headerInner}
              </div>
            ) : null}
          </div>
        </header>
        <div className={`mx-auto min-w-0 ${contentWidth} ${className}`}>{children}</div>
      </>
    );
  }

  return (
    <div className={`mx-auto w-full min-w-0 ${contentWidth} ${className}`}>
      {headerInner ? <header className="mb-8">{headerInner}</header> : null}
      {children}
    </div>
  );
}
